import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Channels } from '@/components/pages/channels';
import { Dashboard } from '@/components/pages/dashboard';
import { EditCampaign } from '@/components/pages/edit-campaign';
import { Funnel } from '@/components/pages/funnel';
import { JobPosting } from '@/components/pages/job-posting';
import { Layout } from '@/components/pages/layout';
import { OpeningDetails } from '@/components/pages/opening-details';
import { Openings } from '@/components/pages/openings';
import { Settings } from '@/components/pages/settings';
import { AutomationSettings } from '@/components/pages/settings/automation';
import { ChannelSettings } from '@/components/pages/settings/channels';
import { Contracts } from '@/components/pages/settings/contracts';
import { DashboardSettings } from '@/components/pages/settings/dashboard';
import { NotFound } from '@/components/ui/not-found';
import { OpeningProvider } from '@/contexts/opening-context';
import { useAccountSlug } from '@/hooks/use-account-slug';

import { RouteAuth } from './route-auth';

function getSharedRoutes() {
  return (
    <>
      <Route element={<Layout />}>
        <Route index element={<Navigate to="dashboard" replace />} />

        {/* All Openings */}
        <Route path="openings" element={<Openings />} />

        {/* Dashboard */}
        <Route path="dashboard" element={<Dashboard />} />

        {/* Settings */}
        <Route path="settings" element={<Settings />}>
          <Route index element={<Navigate to="automation" replace />} />
          <Route path="automation" element={<AutomationSettings />} />
          <Route path="channels" element={<ChannelSettings />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="dashboard" element={<DashboardSettings />} />
        </Route>

        {/* Opening Details */}
        <Route path="dashboard/:openingSlug" element={<OpeningDetails />}>
          <Route index element={<RedirectToChannels />} />
          <Route path="channels" element={<Channels />}>
            <Route
              path=":channelSlug/:sourcingPurchaseId/edit"
              element={<EditCampaign />}
            />
          </Route>
          <Route path="funnel" element={<Funnel />} />
        </Route>
      </Route>

      <Route path="post-job" element={<JobPosting />} />

      {/* Special route to edit Reach campaign as the Reach form is different from other channels */}
      <Route
        path="dashboard/:openingSlug/channels/reach/:sourcingPurchaseId/edit"
        element={
          <Suspense fallback={null}>
            <OpeningProvider>
              <EditCampaign />
            </OpeningProvider>
          </Suspense>
        }
      />

      {/* Fallback route */}
      <Route path="*" Component={NotFound} />
    </>
  );
}

/**
 * Index redirect for `/dashboard/:openingSlug` → `…/channels`. Preserves the
 * current `?hiring_goal_id=…` (and any other query string) so a deep link
 * like `/dashboard/foo?hiring_goal_id=abc` lands on channels with the goal
 * still pinned.
 */
function RedirectToChannels() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: 'channels', search }} replace />;
}

export function AppRoutes() {
  const accountSlug = useAccountSlug();

  return (
    <Routes>
      {/* Navigate to dashboard when hitting the root path ("/")  */}
      <Route
        index
        element={<Navigate to={`/${accountSlug}/dashboard`} replace />}
      />

      {/* Routes with commit SHA and account slug - more specific route first */}
      <Route path=":commitHash/:accountSlug/*" element={<RouteAuth />}>
        {getSharedRoutes()}
      </Route>

      {/* Routes with account slug only */}
      <Route path=":accountSlug/*" element={<RouteAuth />}>
        {getSharedRoutes()}
      </Route>
    </Routes>
  );
}
